import React from 'react';
import { useLocation, Navigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserProfile from './components/Users/UserProfile';
import UserConfiguration from './components/Users/UserConfiguration';
import UserAdministration from './components/Admin/User/UserAdministration';
import WebhookList from './components/Admin/Webhooks/WebhookList';
import { ROUTES } from './routes';
import { useAuth } from './context/AuthContext';

const User = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const { isAuthenticated, loading, isAdmin } = useAuth();

    // Show loading state or redirect if not authenticated
    if (loading) {
        return (
            <div className="min-h-screen bg-panel-gray pt-20 flex justify-center items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-green"></div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }

    const isSettings = location.pathname.includes('settings');
    // Admin route check: Must be /admin but NOT /admin/webhooks (unless we treat webhooks as part of admin dashboard?)
    // Actually, WebhookList is separate.
    const isWebhooksRoute = location.pathname.includes('webhooks');
    const isAdminRoute = location.pathname.includes('admin') && !isWebhooksRoute;

    return (
        <div className="min-h-screen bg-panel-gray pt-24 pb-12">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">{t('UserPage.title')}</h1>
                    <p className="text-gray-500 mt-1">{t('UserPage.subtitle')}</p>
                </div>

                <div className="flex flex-col md:flex-row gap-8">
                    {/* Sidebar Navigation */}
                    <div className="w-full md:w-64 flex-shrink-0">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-24">
                            <nav className="flex flex-col p-2">
                                <Link
                                    to={`${ROUTES.USER}/profile`}
                                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${!isSettings && !isAdminRoute && !isWebhooksRoute
                                        ? 'bg-green-50 text-primary-green'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    {t('UserPage.nav_profile')}
                                </Link>
                                <Link
                                    to={`${ROUTES.USER}/settings`}
                                    className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${isSettings
                                        ? 'bg-green-50 text-primary-green'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {t('UserPage.nav_settings')}
                                </Link>

                                {isAdmin() && (
                                    <>
                                        <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider mt-2">
                                            Admin Zone
                                        </div>
                                        <Link
                                            to={`${ROUTES.USER}/admin`}
                                            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${isAdminRoute
                                                ? 'bg-green-50 text-primary-green'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                }`}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                            {t('UserPage.nav_admin')}
                                        </Link>
                                        <Link
                                            to={ROUTES.WEBHOOKS}
                                            className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${isWebhooksRoute
                                                ? 'bg-green-50 text-primary-green'
                                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                }`}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                            System Webhooks
                                        </Link>
                                    </>
                                )}
                            </nav>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="flex-1">
                        {isWebhooksRoute
                            ? <WebhookList />
                            : (isAdminRoute ? <UserAdministration /> : (isSettings ? <UserConfiguration /> : <UserProfile />))
                        }
                    </div>
                </div>
            </div>
        </div>
    );
};

export default User;
